import { useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { TokenSprite } from "@/components/TokenSprite";
import { useRun } from "@/state/run";
import { useAffinity, AFFINITY_MAX } from "@/state/affinity";
import { useWeeklySelection } from "@/state/weeklySelection";

/**
 * HUD 좌측 "동행 중인 운명" — 2×3 아이콘 그리드 (2026-04-22 E-compact 개편).
 *
 * <p>기존: 6행 리스트 (토큰 + 이름 + 친밀도 점) → 세로 공간 ~320px 차지.
 * <p>신규: <strong>2열 3행 아이콘 그리드</strong> + hover 시 이름·친밀도 툴팁.
 *   - 공간 70% 절감 (Slay the Spire / 프리코네 방식)
 *   - 이번 주 파견된 운명은 glow + pulse
 *   - MAX 친밀도는 토큰 자체 하이라이트
 *   - affinity 점 4개는 토큰 아래 얇은 바 형태로 축약
 */

// 빈 배열은 모듈 수준에서 한 번만 — 참조 안정성 유지 (#185 루프 방지).
const EMPTY_DESTINY_IDS: string[] = [];

export function DestinyTokenStrip() {
    const run = useRun(s => s.run);
    const getAffinity = useAffinity(s => s.getFor);
    const storeActiveIds = useWeeklySelection(s => s.activeDestinyIds);
    const storeRunId = useWeeklySelection(s => s.runId);
    const activeDestinyIds = storeRunId === run?.id ? storeActiveIds : EMPTY_DESTINY_IDS;
    const hasActiveLink = activeDestinyIds.length > 0;
    const [hoverId, setHoverId] = useState<string | null>(null);

    if (!run || run.destinies.length === 0) return null;

    // 서버 destinyStates.affinity 우선. 없으면 클라이언트 스토어 fallback.
    const serverAffinity = new Map<string, number>();
    for (const ds of run.destinyStates ?? []) {
        if (typeof ds.affinity === "number") serverAffinity.set(ds.destinyId, ds.affinity);
    }
    const resolveAffinity = (destinyId: string) =>
        serverAffinity.get(destinyId) ?? getAffinity(run.id, destinyId);

    const hovered = hoverId ? run.destinies.find(d => d.id === hoverId) : null;
    const hoveredAffinity = hovered ? resolveAffinity(hovered.id) : 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: tokens.easing.decelerate }}
            className="relative rounded-m p-s"
            style={{
                background: `${tokens.color.ink}AA`,
                border: `1px solid ${tokens.color.parchmentDim}30`,
                backdropFilter: "blur(3px)",
                boxShadow: hasActiveLink ? `0 0 18px ${tokens.color.ember}12` : `0 0 18px ${tokens.color.ink}44`,
            }}
        >
            <div className="flex items-center justify-between gap-s mb-xs">
                <div
                    className="font-display"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: 13.5,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    동행 운명 · {run.destinies.length}
                </div>
                {hasActiveLink && (
                    <motion.div
                        className="rounded-pill px-s py-[2px] font-body"
                        animate={{ opacity: [0.5, 0.9, 0.5] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                        style={{
                            color: tokens.color.ember,
                            background: `${tokens.color.ember}14`,
                            border: `1px solid ${tokens.color.ember}44`,
                            fontSize: 13,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        이번 주 동행 {activeDestinyIds.length}
                    </motion.div>
                )}
            </div>
            <div
                className="grid gap-xs"
                style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
            >
                {run.destinies.map(d => {
                    const affinity = resolveAffinity(d.id);
                    const maxed = affinity >= AFFINITY_MAX;
                    const linked = activeDestinyIds.includes(d.id);
                    const frame = maxed ? "ember" : linked ? "ember" : "brass";
                    return (
                        <motion.button
                            type="button"
                            key={d.id}
                            onMouseEnter={() => setHoverId(d.id)}
                            onMouseLeave={() => setHoverId(cur => cur === d.id ? null : cur)}
                            onFocus={() => setHoverId(d.id)}
                            onBlur={() => setHoverId(cur => cur === d.id ? null : cur)}
                            className="relative flex flex-col items-center gap-[3px] rounded-s px-[4px] py-[6px]"
                            style={{
                                background: linked
                                    ? `${tokens.color.ember}10`
                                    : "transparent",
                                border: `1px solid ${linked
                                    ? tokens.color.ember + "44"
                                    : tokens.color.parchmentDim + "22"}`,
                                cursor: "pointer",
                            }}
                            animate={linked ? {
                                boxShadow: [
                                    `0 0 0 ${tokens.color.ember}00`,
                                    `0 0 10px ${tokens.color.ember}33`,
                                    `0 0 0 ${tokens.color.ember}00`,
                                ],
                            } : { boxShadow: "none" }}
                            transition={{ duration: 2.2, repeat: linked ? Infinity : 0, ease: "easeInOut" }}
                            title={`${d.name} · 친밀도 ${affinity}/${AFFINITY_MAX}${linked ? " · 이번 주 동행" : ""}`}
                            aria-label={`${d.name}, 친밀도 ${affinity} / ${AFFINITY_MAX}`}
                        >
                            <TokenSprite
                                kind="destiny"
                                id={d.id}
                                size={28}
                                highlighted={maxed || linked}
                                rarity={frame}
                            />
                            {/* 친밀도 미니 바 — 4단계 점 대신 연속 바로 압축. */}
                            <div
                                className="w-full rounded-pill overflow-hidden"
                                style={{ height: 3, background: `${tokens.color.steel}33` }}
                            >
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${(affinity / AFFINITY_MAX) * 100}%` }}
                                    transition={{ duration: 0.5, ease: tokens.easing.decelerate }}
                                    style={{
                                        height: "100%",
                                        background: maxed
                                            ? tokens.color.ember
                                            : linked
                                                ? tokens.color.ember
                                                : tokens.color.brass,
                                        boxShadow: (maxed || linked) ? `0 0 4px ${tokens.color.ember}AA` : "none",
                                    }}
                                />
                            </div>
                        </motion.button>
                    );
                })}
            </div>
            {/* Hover 툴팁 — 그리드 바로 아래. 이름 + 친밀도 점 4단 표시. */}
            {hovered && (
                <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    className="mt-xs rounded-s px-s py-xs"
                    style={{
                        background: `${tokens.color.ink}E6`,
                        border: `1px solid ${tokens.color.parchmentDim}44`,
                    }}
                >
                    <div
                        className="font-display"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: 13.5,
                            letterSpacing: tokens.type.letterSpacing.wide,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        {hovered.name}
                    </div>
                    <div className="mt-[3px] flex items-center gap-[3px]">
                        {Array.from({ length: AFFINITY_MAX }).map((_, i) => {
                            const filled = i < hoveredAffinity;
                            return (
                                <span
                                    key={i}
                                    style={{
                                        width: 10,
                                        height: 4,
                                        borderRadius: 2,
                                        background: filled
                                            ? (hoveredAffinity >= AFFINITY_MAX ? tokens.color.ember : tokens.color.brass)
                                            : `${tokens.color.steel}55`,
                                    }}
                                />
                            );
                        })}
                        <span
                            className="ml-xs font-body"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: 13,
                                letterSpacing: tokens.type.letterSpacing.wide,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            친밀도 {hoveredAffinity}/{AFFINITY_MAX}
                        </span>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
}
